/**
 * Apply a data-analytics-id attribute to an element to have its click events
 * automatically tracked.
 *
 * An optional `data-analytics-attributes` attribute may also be applied to
 * the element to send custom attributes with the `trackEvent`. The contents
 * of this should be a JSON.stringified object.
 *
 * e.g.
 * // Will generate an event named "Get Started" with the vehicle ID and an
 * action property equal to "clicked".
 * <button
 *   type="button"
 *   data-analytics-id="Get Started"
 *   data-analytics-attributes={JSON.stringify({ vehicleId: 1234 })}
 * >
 *   Get Started
 * </button>
 *
 * In cases where event propagation is stopped, use the `useTrackClickEvent`
 * hook to create an event handler that can be called in the click handler
 * that stopped propagation.
 *
 * e.g.
 * const trackClickEvent = useTrackClickEvent(trackEvent);
 *
 * function onClick(ev: MouseEvent) {
 *  ev.stopPropagation();
 *  trackClickEvent(ev);
 *  // Do other stuff
 * }
 */
import { useCallback, useEffect } from 'react';

type TrackEvent<CustomAttributes = object> = (
  eventName: string,
  customAttributes?: CustomAttributes | undefined
) => void;

function safeParse<T = any>(input: string): T | undefined {
  try {
    return JSON.parse(input);
  } catch (ex) {
    return undefined;
  }
}

const fetchBodyElement = () => document.body;

// Module-level: shared across all useTrackClickEvents instances.
const DOUBLE_CLICK_GUARD_MS = 500;
let lastEventId = '';
let lastEventTime = 0;

export function useTrackClickEvent<CustomAttributes extends object>(
  trackEvent: TrackEvent<CustomAttributes>,
  fireDataDogAction?: (actionName: string, context?: CustomAttributes) => void
) {
  return useCallback(
    (ev: MouseEvent) => {
      if (!(ev.target instanceof Element)) return;

      const target = ev.target.closest<HTMLElement>('[data-analytics-id]');
      const { analyticsId, analyticsAttributes } = target?.dataset || {};

      if (!analyticsId) return;

      const now = Date.now();
      const isDoubleClick =
        analyticsId === lastEventId &&
        now - lastEventTime < DOUBLE_CLICK_GUARD_MS;

      lastEventId = analyticsId;
      lastEventTime = now;

      if (isDoubleClick) return;

      const context = {
        ...(analyticsAttributes ? safeParse(analyticsAttributes) : undefined),
        action: 'clicked'
      };

      trackEvent(analyticsId, context);

      if (fireDataDogAction) {
        fireDataDogAction(analyticsId, context);
      }
    },
    [fireDataDogAction, trackEvent]
  );
}

export default function useTrackClickEvents<CustomAttributes extends object>(
  trackEvent: TrackEvent<CustomAttributes>,
  fetchContainerElement: () => HTMLElement | null = fetchBodyElement,
  fireDataDogAction?: (actionName: string, context?: CustomAttributes) => void
) {
  const trackClickEvent = useTrackClickEvent(trackEvent, fireDataDogAction);

  useEffect(() => {
    function onClick(ev: MouseEvent) {
      if (!(ev.target instanceof Element)) return;

      // Capture phase (true flag below) runs before onClick handlers, so the
      // target is still in the DOM when we scope-check it.
      if (!fetchContainerElement()?.contains(ev.target)) return;

      trackClickEvent(ev);
    }

    document.addEventListener('click', onClick, true);
    document.addEventListener('contextmenu', onClick, true);

    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('contextmenu', onClick, true);
    };
  }, [fetchContainerElement, trackClickEvent]);
}
